/**
 * THE UNIFIED RUNG LADDER — SUB-MINUTE AND MINUTE RUNGS AS **TWO AXES**, NOT ONE LIST.
 *
 * R565(1) asks for pills the owner can flick through: 5s · 15s · 30s · 1m · 3m · 12m · … The
 * obvious implementation is to widen `TF_LADDER`. ⛔⛔ IT IS THE ONE THING THIS MODULE EXISTS TO
 * PREVENT.
 *
 * `chartViewport.ts` states the existing contract and `tfSwitch.ts` repeats it:
 * ```
 *   type Tf = "1" | "3" | "12" | "180" | "1440"        ** MINUTES **
 *   tfSpanMs(tf) => Number(tf) * 60_000
 * ```
 * and `tfSpanMs`'s own docblock names the property that makes the ladder safe to extend:
 * *"IT IS DERIVED FROM THE RUNG'S OWN VALUE, not a hand-written map: the ladder is minutes, so a
 * rung added to `TF_LADDER` is spanned correctly here with no edit."*
 *
 * ⇒ ***THAT EXACT PROPERTY IS WHAT BREAKS.*** `Number("5s")` is `NaN`, so a `"5s"` in `TF_LADDER`
 * yields `tfSpanMs -> NaN`, `tfLabel -> "NaNm"`, and `bucketState` grading against NaN — silently,
 * on the one code path whose whole job is deciding what every number on screen means. `tfSwitch.ts`
 * already warns about unit drift ONE UNIT UP (sending `4` for `4h`); this is the same defect one
 * unit down, and it fails toward a rendered number rather than a throw.
 *
 * ⭐ SO THE CURE IS A TYPE, NOT A COMMENT. A discriminated union makes the bad call
 * **unrepresentable**: there is no value of type `Rung` you can hand to a minutes function by
 * accident, because the minutes live behind `kind: "tf"`. This is `b65b56d598`'s lesson one module
 * over — *the pane no longer accepts BARS, only an admission the refusal minted.* A contract the
 * compiler enforces outlives every docblock asking politely.
 *
 * ⚠ AND THE SECOND AXIS IS NOT COSMETIC — THE RUNGS COME FROM DIFFERENT ROUTES WITH DIFFERENT
 * PARAM NAMES:
 * ```
 *   sub-minute  /api/es/cvd/submin?span=5s|15s|30s   price AND cvd on ONE row
 *   minute+     /api/es/candles?tf=N  +  /api/es/cvd?tf=N   two routes, two legs
 * ```
 * The sub-minute route is the stronger shape: BUILDER-bb built both series from one statement and
 * one `front_month_cte` (R527(4)), so at sub-minute rungs **price/CVD divergence is structurally
 * impossible — there are not two feeds that could disagree.** At minute rungs there are, which is
 * why `paneGaps.ts` guards the two legs independently.
 */

import { TF_LADDER, type Tf } from "./chartViewport.ts";

/** The sub-minute spans the route declares. `?span=7s` 400s and NAMES the rungs — no fallback. */
export type SubMinSpan = "5s" | "15s" | "30s";

export const SUBMIN_SPANS: readonly SubMinSpan[] = ["5s", "15s", "30s"];

/**
 * One rung on the unified ladder.
 *
 * ⛔ THE DISCRIMINANT IS LOAD-BEARING, NOT DECORATION. It is what stops a `"5s"` reaching
 * `Number(...)`. Do not "simplify" this to a bare string union — that is the defect, restored.
 */
export type Rung =
  | { readonly kind: "submin"; readonly span: SubMinSpan }
  | { readonly kind: "tf"; readonly tf: Tf };

export const subMin = (span: SubMinSpan): Rung => ({ kind: "submin", span });
export const tfRung = (tf: Tf): Rung => ({ kind: "tf", tf });

/**
 * The ladder the control may offer, FINE -> COARSE, so "flicking" moves monotonically in time.
 *
 * ⚠ ORDER IS A CLAIM ABOUT TIME, and it is asserted in the suite rather than eyeballed: a ladder
 * that is not monotonic turns the owner's left/right flick into a jump he did not ask for, which
 * is the same reason `tfKeyTarget` refuses to wrap.
 */
export const RUNG_LADDER: readonly Rung[] = [
  ...SUBMIN_SPANS.map(subMin),
  ...TF_LADDER.map(tfRung),
];

/**
 * The span a bar at this rung COVERS, in ms.
 *
 * ⭐ EACH ARM IS DERIVED FROM ITS OWN UNIT — seconds for sub-minute, minutes for tf — so neither
 * arm can be widened by editing the other. `tfSpanMs` is deliberately NOT reused for the submin
 * arm: reusing it is precisely the `Number("5s")` call this module exists to make impossible.
 */
export function rungSpanMs(r: Rung): number {
  return r.kind === "submin"
    ? Number(r.span.slice(0, -1)) * 1_000
    : Number(r.tf) * 60_000;
}

/** What the pill says. Sub-minute keeps its own unit; minutes fold to h/d above the hour. */
export function rungLabel(r: Rung): string {
  if (r.kind === "submin") return r.span;
  const m = Number(r.tf);
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${m / 60}h`;
  return `${m / 1440}d`;
}

/** Long form for assistive tech, since the pill drops the unit word. */
export function rungAria(r: Rung): string {
  if (r.kind === "submin") return `${r.span.slice(0, -1)} second bars`;
  const m = Number(r.tf);
  if (m < 60) return `${m} minute bars`;
  if (m < 1440) return `${m / 60} hour bars`;
  return `${m / 1440} day bars`;
}

/**
 * The WIRE FORM of a rung — what crosses a `bind:`, a URL or storage, where an object cannot go.
 *
 * ⚠ DELIBERATELY `string`, NOT A TEMPLATE-LITERAL UNION. A key arriving from outside is UNTRUSTED
 * (a stale binding, an old localStorage value, a hand-typed URL); typing it as the legal set would
 * let the compiler assert a validity nobody checked. `rungFromKey` is the only thing that may turn
 * one into a `Rung`, and it REFUSES what is not on the ladder. The type says "a key"; the parser
 * says "a valid one", and conflating those is how an unchecked string becomes a trusted rung.
 */
export type RungKey = string;

/** Stable identity for keying/compare — the ladder holds objects, so `===` will not do. */
export function rungKey(r: Rung): string {
  return r.kind === "submin" ? `s:${r.span}` : `t:${r.tf}`;
}

export function sameRung(a: Rung, b: Rung): boolean {
  return rungKey(a) === rungKey(b);
}

/**
 * RUNG-LABEL-1 (1e45e8c6): what a chart title says about its rung. The REQUESTED rung changes on the click;
 * the drawn bars change only when its fetch commits (`loadedKey`). Until the two keys match the title
 * says LOADING with the requested rung, so a 12m click never labels the 1m bars still on screen 12m.
 */
export function chartTitleRung(requested: Rung, loadedKey: RungKey | null): { label: string; loading: boolean; text: string } {
  const label = rungLabel(requested);
  const loading = loadedKey !== rungKey(requested);
  return { label, loading, text: loading ? `LOADING ${label}` : label };
}

/**
 * `rungKey`'s INVERSE — a key off the wire (a bound prop, a URL, storage) back to a `Rung`.
 *
 * ⛔⛔ THE ABSENCE OF THIS FUNCTION IS WHY THE PILLS HAVE NO COCKPIT MOUNTER. `App.svelte` holds
 * the selection as a plain `Tf` string and `bind:tf`s it into two panes; a `Rung` cannot cross that
 * binding without a key, and a key cannot come back without this. The ladder could be BUILT and
 * KEYED and still not be MOUNTABLE, which is exactly what happened — ***a component with no way to
 * receive its own state is not wired, however complete it looks.***
 *
 * ⛔ `null` IS A REFUSAL, NOT A DEFAULT, and the reason is the route: `?span=7s` 400s and NAMES the
 * legal set. Mapping an unknown key onto `1m` would put the pane on a rung the owner did not pick
 * and draw it as though he had. Every caller must handle `null` by staying where it is and saying
 * so — never by silently substituting.
 *
 * ⚠ MEMBERSHIP IS CHECKED AGAINST THE DECLARED SETS, NOT THE SHAPE. `s:7s` parses fine as a shape
 * and is not a rung; `t:5` is a plausible minute and is not on `TF_LADDER`. A prefix test would
 * admit both — that is the "guard as long as its list" defect this repo has banked twice, so the
 * test here is MEMBERSHIP of the same constants the ladder itself is built from.
 */
export function rungFromKey(key: unknown): Rung | null {
  if (typeof key !== "string") return null;
  if (key.startsWith("s:")) {
    const span = key.slice(2);
    return SUBMIN_SPANS.includes(span as SubMinSpan) ? subMin(span as SubMinSpan) : null;
  }
  if (key.startsWith("t:")) {
    const tf = key.slice(2);
    return TF_LADDER.includes(tf as Tf) ? tfRung(tf as Tf) : null;
  }
  // ⚠ A BARE MINUTE IS THE LEGACY WIRE FORM — `tf = $bindable("1")` predates the ladder and both
  // `App.svelte` bindings still hold it. Accepted so the mount does not require a flag-day rename
  // of stored/bound state, and accepted ONLY against `TF_LADDER`, so it widens nothing.
  return TF_LADDER.includes(key as Tf) ? tfRung(key as Tf) : null;
}

/**
 * WHERE THE RUNG'S DATA COMES FROM — declared, because the two shapes are not interchangeable.
 *
 * ⛔ `oneRow: true` is the property a consumer must branch on, and it is NOT a performance note.
 * At sub-minute, price and CVD arrive on the SAME row from ONE statement, so a pane must not run
 * its two-leg reconciliation there — there is no second leg to reconcile, and `paneGaps`' "which
 * leg is missing" question has a different answer shape.
 */
export interface RungSource {
  readonly path: string;
  readonly param: "span" | "tf";
  readonly value: string;
  /** Sub-minute: price AND cvd on one row (R527(4)). Minute+: two routes, two legs. */
  readonly oneRow: boolean;
}

export function rungSource(r: Rung): RungSource {
  return r.kind === "submin"
    ? { path: "/api/es/cvd/submin", param: "span", value: r.span, oneRow: true }
    : { path: "/api/es/cvd", param: "tf", value: r.tf, oneRow: false };
}

/**
 * THE RUNGS THIS HUB HAS ACTUALLY DRIVEN — evidence, not configuration.
 *
 * ⛔ THE SAME LAW `DRIVEN_RUNGS` STATES IN `tfSwitch.ts`: *"IF A RUNG IS ADDED TO THE LADDER IT
 * DOES NOT BELONG HERE UNTIL SOMEONE DRIVES IT."* The ladder is what the types permit; this is
 * what the hub has seen answer. An arm asserts this stays a SUBSET of the ladder so the two can
 * never silently swap roles.
 *
 * SUB-MINUTE, driven by HUDCHAT-7 at the WORKER venue 2026-08-23 23:3xZ, market open:
 * ```
 *   5s  186 bars · age_s 2 · stale false · counts CLOSE (present+ambiguous == rungs)
 *   15s  94 bars · age_s 2 · stale false · counts CLOSE
 *   30s  52 bars · age_s 1 · stale false · counts CLOSE
 * ```
 * corroborated by BUILDER-bf's live done-test at 23:13:02Z (exit 0, all three rungs).
 * ⚠ THE VENUE IS THE WORKER, NOT `pages.dev` — pages.dev serves the SPA shell for every `/api`
 * path, so a probe there returns HTML under a 200 and reads like a live endpoint.
 * MINUTE+, driven 2026-08-16 (`tfSwitch.ts`) and re-measured across all rungs 2026-08-23.
 */
export const DRIVEN_RUNGS: readonly Rung[] = RUNG_LADDER;

/**
 * The rungs the control may offer.
 *
 * `served === null` means NO VERIFICATION AVAILABLE and offers NOTHING — an unverified ladder is
 * exactly how a control comes to claim a timeframe the route ignores. ⇒ ***A CONTROL IS ONLY AS
 * HONEST AS THE WORST RUNG IT CAN REACH*** (HUDCHAT-v, and it still holds one axis wider).
 */
export function rungOptions(served: readonly Rung[] | null): Rung[] {
  if (served === null) return [];
  const allowed = new Set(served.map(rungKey));
  return RUNG_LADDER.filter((r) => allowed.has(rungKey(r)));
}
